<script lang="ts" setup>
/**
 * 通用 AI 工具落地页（参考 ai-undress 多段式：Hero + Gallery + Features + FAQ + CTA）
 * 子组件 / 段落由 config prop 数据驱动；交互区域走 default slot 注入。
 */
interface ImageItem { src: string; alt?: string; title?: string; description?: string; video?: string }
interface FeatureItem {
  title: string;
  description: string;
  image?: string;
  video?: { src: string; poster?: string };
}
interface FaqItem { question: string; answer: string }

interface Config {
  hero: { title: string; description: string; videoSrc?: string; videoPoster?: string };
  stats?: { items: { value: string; label: string }[] };
  gallery?: { title: string; description?: string; items: ImageItem[] };
  imageFeatures?: { title: string; description?: string; items: FeatureItem[] };
  introduce?: { title: string; description: string; image?: string };
  benefit?: { title: string; description: string; image?: string };
  usage?: { title: string; description?: string; steps: { title: string; description: string }[] };
  faq?: { title: string; items: FaqItem[] };
  cta?: { title: string; description?: string; buttonText: string; buttonHref?: string };
}

defineProps<{ config: Config }>();
const openFaq = ref<number | null>(0);
function toggleFaq(i: number) { openFaq.value = openFaq.value === i ? null : i; }
</script>

<template>
  <div class="landing">
    <!-- ============ Hero ============ -->
    <section class="hero">
      <div class="hero-grid">
        <!-- 左：标题 + 视频/介绍 -->
        <div class="hero-left">
          <h1 class="hero-title">
            <span class="grad">{{ config.hero.title }}</span>
          </h1>
          <p class="hero-desc">{{ config.hero.description }}</p>
          <div v-if="config.hero.videoSrc" class="hero-media">
            <video
              :src="config.hero.videoSrc"
              :poster="config.hero.videoPoster"
              autoplay loop muted playsinline
              class="size-full object-cover rounded-xl"
            />
          </div>
        </div>
        <!-- 右：交互（上传/表单），由父组件 slot 注入 -->
        <div class="hero-right">
          <slot name="form" />
        </div>
      </div>
    </section>

    <!-- ============ Stats ============ -->
    <section v-if="config.stats" class="stats">
      <div class="stats-grid">
        <div v-for="s in config.stats.items" :key="s.label" class="stat">
          <div class="stat-value">{{ s.value }}</div>
          <div class="stat-label">{{ s.label }}</div>
        </div>
      </div>
    </section>

    <!-- ============ Gallery ============ -->
    <section v-if="config.gallery" class="block">
      <div class="block-head">
        <h2>{{ config.gallery.title }}</h2>
        <p v-if="config.gallery.description">{{ config.gallery.description }}</p>
      </div>
      <div class="gallery-grid">
        <div v-for="(g, i) in config.gallery.items" :key="i" class="gallery-item">
          <video
            v-if="g.video"
            :src="g.video"
            :poster="g.src"
            autoplay loop muted playsinline
            preload="metadata"
          />
          <img v-else :src="g.src" :alt="g.alt || ''" loading="lazy" />
          <div v-if="g.title" class="gallery-label">{{ g.title }}</div>
        </div>
      </div>
    </section>

    <!-- ============ Image Features ============ -->
    <section v-if="config.imageFeatures" class="block">
      <div class="block-head">
        <h2>{{ config.imageFeatures.title }}</h2>
        <p v-if="config.imageFeatures.description">{{ config.imageFeatures.description }}</p>
      </div>
      <div class="feat-grid">
        <div v-for="(f, i) in config.imageFeatures.items" :key="i" class="feat-card">
          <div class="feat-media">
            <video v-if="f.video"
              :src="f.video.src" :poster="f.video.poster"
              autoplay loop muted playsinline />
            <img v-else-if="f.image" :src="f.image" :alt="f.title" />
          </div>
          <h3>{{ f.title }}</h3>
          <p>{{ f.description }}</p>
        </div>
      </div>
    </section>

    <!-- ============ Introduce (Feature1) ============ -->
    <section v-if="config.introduce" class="block split">
      <div class="split-text">
        <h2>{{ config.introduce.title }}</h2>
        <p>{{ config.introduce.description }}</p>
      </div>
      <div v-if="config.introduce.image" class="split-media">
        <img :src="config.introduce.image" alt="" />
      </div>
    </section>

    <!-- ============ Benefit (Feature2, 反向布局) ============ -->
    <section v-if="config.benefit" class="block split reverse">
      <div class="split-text">
        <h2>{{ config.benefit.title }}</h2>
        <p>{{ config.benefit.description }}</p>
      </div>
      <div v-if="config.benefit.image" class="split-media">
        <img :src="config.benefit.image" alt="" />
      </div>
    </section>

    <!-- ============ Usage (Feature3, 步骤式) ============ -->
    <section v-if="config.usage" class="block">
      <div class="block-head">
        <h2>{{ config.usage.title }}</h2>
        <p v-if="config.usage.description">{{ config.usage.description }}</p>
      </div>
      <div class="step-grid">
        <div v-for="(s, i) in config.usage.steps" :key="i" class="step">
          <div class="step-num">{{ i + 1 }}</div>
          <h3>{{ s.title }}</h3>
          <p>{{ s.description }}</p>
        </div>
      </div>
    </section>

    <!-- ============ FAQ ============ -->
    <section v-if="config.faq" class="block">
      <div class="block-head">
        <h2>{{ config.faq.title }}</h2>
      </div>
      <div class="faq">
        <div v-for="(q, i) in config.faq.items" :key="i" class="faq-item" :class="{ open: openFaq === i }">
          <button class="faq-q" @click="toggleFaq(i)">
            <span>{{ q.question }}</span>
            <span class="faq-icon">{{ openFaq === i ? '−' : '+' }}</span>
          </button>
          <div v-show="openFaq === i" class="faq-a">{{ q.answer }}</div>
        </div>
      </div>
    </section>

    <!-- ============ CTA ============ -->
    <section v-if="config.cta" class="cta">
      <h2>{{ config.cta.title }}</h2>
      <p v-if="config.cta.description">{{ config.cta.description }}</p>
      <NuxtLink v-if="config.cta.buttonHref" :to="config.cta.buttonHref" class="cta-btn">
        {{ config.cta.buttonText }}
      </NuxtLink>
      <a v-else href="#hero" class="cta-btn" @click.prevent="$el.scrollIntoView({behavior:'smooth'})">
        {{ config.cta.buttonText }}
      </a>
    </section>
  </div>
</template>

<style scoped lang="scss">
.landing { @apply text-white; max-width: 1200px; margin: 0 auto; padding: 0 1rem; }
@media (min-width: 768px) { .landing { padding: 0 1.5rem; } }

/* ===== Hero ===== */
.hero { @apply pt-28 md:pt-36 pb-12; }
.hero-grid {
  display: grid;
  gap: 2rem;
  grid-template-columns: minmax(0, 1fr);
}
@media (min-width: 1024px) {
  .hero-grid { grid-template-columns: 1fr 1fr; align-items: stretch; }
}
.hero-right { display: flex; }
.hero-right > * { flex: 1; }
.hero-title { @apply text-3xl md:text-5xl font-extrabold leading-tight mb-4; }
.grad {
  background: linear-gradient(135deg, #f97316 0%, #fbbf24 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.hero-desc { @apply text-white/70 text-base md:text-lg mb-6; }
.hero-media { @apply rounded-xl overflow-hidden border border-neutral-800 bg-black; aspect-ratio: 16/9; }

/* ===== Stats ===== */
.stats { @apply py-10; }
.stats-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
@media (min-width: 768px) { .stats-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
.stat { @apply text-center rounded-2xl border border-neutral-800 bg-neutral-900/40 p-6; }
.stat-value { @apply text-3xl md:text-4xl font-bold text-amber-400; }
.stat-label { @apply mt-2 text-xs uppercase tracking-wider text-white/50; }

/* ===== Block (gallery / features / etc.) ===== */
.block { @apply py-12 md:py-16; }
.block-head { @apply text-center mb-8 md:mb-10; }
.block-head h2 { @apply text-2xl md:text-4xl font-bold mb-3; }
.block-head p { @apply text-white/60 text-sm md:text-base max-w-2xl mx-auto; }

/* ===== Gallery ===== */
.gallery-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
@media (min-width: 768px) { .gallery-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
.gallery-item { @apply relative rounded-2xl overflow-hidden border border-neutral-800 bg-neutral-900; aspect-ratio: 1/1; }
.gallery-item img,
.gallery-item video { @apply size-full object-cover hover:scale-105 transition-transform duration-500; }
.gallery-label {
  @apply absolute bottom-2 left-2 right-2 text-center text-sm font-medium text-white
         bg-black/50 backdrop-blur-sm rounded-md px-2 py-1;
}

/* ===== Image Features ===== */
.feat-grid {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: minmax(0, 1fr);
}
@media (min-width: 768px) { .feat-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
.feat-card { @apply rounded-2xl border border-neutral-800 bg-neutral-900/60 overflow-hidden; }
.feat-media { aspect-ratio: 16/9; @apply bg-black; }
.feat-media img, .feat-media video { @apply size-full object-cover; }
.feat-card h3 { @apply px-5 pt-5 text-lg font-semibold; }
.feat-card p { @apply px-5 pb-5 pt-2 text-sm text-white/60; }

/* ===== Split (Feature1/2) ===== */
.split { display: grid; gap: 2rem; align-items: center; }
@media (min-width: 1024px) { .split { grid-template-columns: 1fr 1fr; } }
.split.reverse .split-text { order: 2; }
@media (min-width: 1024px) { .split.reverse .split-text { order: 0; } }
.split-text h2 { @apply text-2xl md:text-3xl font-bold mb-4; }
.split-text p { @apply text-white/70 leading-relaxed; }
.split-media { @apply rounded-2xl overflow-hidden border border-neutral-800 bg-neutral-900; aspect-ratio: 4/3; }
.split-media img { @apply size-full object-cover; }

/* ===== Steps ===== */
.step-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1fr);
}
@media (min-width: 768px) { .step-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
.step { @apply rounded-2xl border border-neutral-800 bg-neutral-900/60 p-6 text-center; }
.step-num {
  @apply mx-auto mb-4 size-12 rounded-full text-xl font-bold flex items-center justify-center text-neutral-950;
  background: linear-gradient(135deg, #f97316 0%, #fbbf24 100%);
}
.step h3 { @apply text-lg font-semibold mb-2; }
.step p { @apply text-sm text-white/60; }

/* ===== FAQ ===== */
.faq { @apply max-w-3xl mx-auto space-y-3; }
.faq-item { @apply rounded-2xl border border-neutral-800 bg-neutral-900/60 overflow-hidden; }
.faq-item.open { @apply border-amber-500/40; }
.faq-q {
  @apply w-full flex items-center justify-between gap-3 px-5 py-4 text-left text-base font-semibold;
}
.faq-icon { @apply text-2xl text-amber-400 leading-none; }
.faq-a { @apply px-5 pb-5 pt-0 text-sm text-white/70 leading-relaxed; }

/* ===== CTA ===== */
.cta {
  @apply text-center rounded-3xl my-12 md:my-16 p-10 md:p-16;
  background: linear-gradient(135deg, rgba(251,146,60,0.10) 0%, rgba(245,158,11,0.05) 100%);
  border: 1px solid rgba(251,146,60,0.2);
}
.cta h2 { @apply text-2xl md:text-4xl font-bold mb-3; }
.cta p { @apply text-white/70 text-sm md:text-base mb-6 max-w-2xl mx-auto; }
.cta-btn {
  @apply inline-block rounded-full px-8 py-4 text-base font-semibold text-white transition-all;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
  box-shadow: 0 8px 24px -8px rgba(249,115,22,0.5);
}
.cta-btn:hover { transform: scale(1.03); }
</style>
